/**
 * Attached-files manifest WIRE strings: the block appended to a `user.message`
 * when uploads become available to the run's session. The agent parses this
 * text, so the marker, the " -> " separator, and the closing sentence are
 * frozen protocol (same rule as the HITL wire strings) — read forever, never
 * reworded. English-only by owner decision: the block is agent-facing, and the
 * console renders its own localized chips from `parseAttachmentManifest`.
 *
 * Dependency-free plain TypeScript on purpose: both the API and the web app
 * import it, and neither should inherit a zod version through it.
 */
export const ATTACHMENT_MANIFEST_MARKER = "[attached-files]";
/** Provider-side root every session resource is mounted under. */
export const SESSION_UPLOADS_ROOT = "/mnt/session/uploads";
const MANIFEST_SEPARATOR = " -> ";
const MANIFEST_ITEM_PREFIX = "- ";
const MANIFEST_FOOTER = `These uploaded files are now available; discover all files under ${SESSION_UPLOADS_ROOT}/ ` +
    "with glob.";
/**
 * Where a stored `mount_path` ("/workspace/ab12cd-name.pdf") materializes
 * inside the session sandbox. Joins with exactly one slash whether or not the
 * stored path is rooted.
 */
export function runtimeUploadPath(mountPath) {
    const relative = mountPath.replace(/^\/+/, "");
    return relative ? `${SESSION_UPLOADS_ROOT}/${relative}` : SESSION_UPLOADS_ROOT;
}
export function buildAttachmentManifestBlock(files) {
    if (files.length === 0)
        throw new Error("attachment manifest requires at least one file");
    const lines = files.map((file) => `${MANIFEST_ITEM_PREFIX}${file.displayName}${MANIFEST_SEPARATOR}${file.runtimePath}`);
    return [ATTACHMENT_MANIFEST_MARKER, ...lines, MANIFEST_FOOTER].join("\n");
}
export function isAttachmentManifestBlock(text) {
    return (text.split("\n", 1)[0] ?? "").trim() === ATTACHMENT_MANIFEST_MARKER;
}
/**
 * Returns null when `text` is not a manifest block. Item lines split at the
 * LAST separator, so a display name may itself contain " -> "; lines that are
 * not items (the footer, blanks, anything unrecognized) are skipped.
 */
export function parseAttachmentManifest(text) {
    if (!isAttachmentManifestBlock(text))
        return null;
    const files = [];
    for (const line of text.split("\n").slice(1)) {
        const trimmed = line.trim();
        if (!trimmed.startsWith(MANIFEST_ITEM_PREFIX))
            continue;
        const item = trimmed.slice(MANIFEST_ITEM_PREFIX.length);
        const separator = item.lastIndexOf(MANIFEST_SEPARATOR);
        if (separator <= 0)
            continue;
        const displayName = item.slice(0, separator);
        const runtimePath = item.slice(separator + MANIFEST_SEPARATOR.length);
        if (!displayName || !runtimePath)
            continue;
        files.push({ displayName, runtimePath });
    }
    return files;
}
//# sourceMappingURL=attachment-manifest.js.map